import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { fontSize, minBreakpointQuery, sectionMargins } from '../styles';
import { Container, HtmlContent } from './ui';

const StyledProductSpecifications = styled.div`
  ${sectionMargins('20px', '40px')};
`;

const StyledHeading = styled.h4`
  margin-bottom: 12px;
  ${fontSize(17)};

  ${minBreakpointQuery.small`
    margin-bottom: 16px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    margin-bottom: 20px;
    ${fontSize(19)};
  `}
`;

const ProductSpecifications = ({ heading, items }) => {
  const {
    datoCmsProductSitewide: {
      specificationsMetafieldExclusions: metafieldExclusions,
      specificationsFormatterItems: formatterItems,
      specificationsOrdererTopItems: ordererTopItems,
      specificationsOrdererBottomItems: ordererBottomItems,
    },
  } = useStaticQuery(graphql`
    query ProductSpecificationsQuery {
      datoCmsProductSitewide {
        specificationsMetafieldExclusions
        specificationsFormatterItems {
          shopifyMetafieldKey
          formattedName
        }
        specificationsOrdererTopItems
        specificationsOrdererBottomItems
      }
    }
  `);

  items.sort(({ key: a }, { key: b }) => {
    const topIndexA = ordererTopItems.indexOf(a);
    const topIndexB = ordererTopItems.indexOf(b);
    const bottomIndexA = ordererBottomItems.indexOf(a);
    const bottomIndexB = ordererBottomItems.indexOf(b);

    if (topIndexA !== -1 && topIndexB !== -1) {
      return topIndexA - topIndexB; // sort within ordererTopItems
    }
    if (topIndexA !== -1) return -1; // a is in ordererTopItems, goes above
    if (topIndexB !== -1) return 1; // b is in ordererTopItems, goes below

    if (bottomIndexA !== -1 && bottomIndexB !== -1) {
      return bottomIndexA - bottomIndexB; // sort within ordererBottomItems
    }
    if (bottomIndexA !== -1) return 1; // a is in ordererBottomItems, goes above
    if (bottomIndexB !== -1) return -1; // b is in ordererBottomItems, goes below

    return 0; // keep original order for middle items
  });

  return (
    items.length > 0 && (
      <StyledProductSpecifications>
        <Container narrow={true}>
          {heading && <StyledHeading>{heading}</StyledHeading>}
          <HtmlContent>
            <table>
              {items
                .filter(({ key }) => !metafieldExclusions.includes(key))
                .map(({ key, value, type }, id) => {
                  const formatKey = key => {
                    const formatterItem = formatterItems.find(
                      element => element.shopifyMetafieldKey === key,
                    );

                    if (formatterItem) {
                      return formatterItem.formattedName;
                    } else {
                      const words = key
                        .replace(' [mutli_line_text_field]', '')
                        .split('_');
                      const formattedKey = words.map(
                        word => word.charAt(0).toUpperCase() + word.slice(1),
                      );

                      return formattedKey.join(' ');
                    }
                  };

                  const formatValue = (value, type) => {
                    const getUnitAbbreviation = unit => {
                      let unitAbbreviation = '';

                      switch (unit) {
                        case 'MILLIMETERS':
                          unitAbbreviation = 'mm';
                          break;
                        case 'CENTIMETERS':
                          unitAbbreviation = 'cm';
                          break;
                        case 'METERS':
                          unitAbbreviation = 'm';
                          break;
                        case 'INCHES':
                          unitAbbreviation = 'in';
                          break;
                        case 'FEET':
                          unitAbbreviation = 'ft';
                          break;
                        case 'YARDS':
                          unitAbbreviation = 'yd';
                          break;
                        case 'POUNDS':
                          unitAbbreviation = 'lb';
                          break;
                        case 'OUNCES':
                          unitAbbreviation = 'oz';
                          break;
                        case 'KILOGRAMS':
                          unitAbbreviation = 'kg';
                          break;
                        case 'GRAMS':
                          unitAbbreviation = 'g';
                          break;
                      }

                      return unitAbbreviation;
                    };

                    const valueUnit =
                      (type === 'dimension' || type === 'weight') &&
                      JSON.parse(value);

                    const formattedValue = valueUnit
                      ? `${valueUnit.value} ${getUnitAbbreviation(
                          valueUnit.unit,
                        )}`
                      : value;

                    return formattedValue;
                  };

                  return (
                    <tr key={id}>
                      <th>{formatKey(key)}:</th>
                      <td>{formatValue(value, type)}</td>
                    </tr>
                  );
                })}
            </table>
          </HtmlContent>
        </Container>
      </StyledProductSpecifications>
    )
  );
};

export default ProductSpecifications;
